/* A merchant signing in: the username and password Damga issued, then, on a new
 * device, the code emailed to them.
 *
 * ONE SCREEN FOR THE DASHBOARD AND THE TILL, which are one product (ruled
 * 2026-09-18). The words come in as a prop because the two surfaces keep them in
 * different tables for a reason: the till's are in the Arabic sheet's scope and
 * the dashboard's are English-only by ruling.
 *
 * WHAT IT NEVER SAYS. Which of the two was wrong. The server refuses a wrong
 * password, an unknown username and a paused sign-in with the same bytes; the kit
 * turns all of them into one outcome; and this shows one sentence for it. A
 * screen that said "no such username" would undo all three.
 *
 * NO GOOGLE, NO MICROSOFT, NO "EMAIL ME A CODE". The customer's doors. The code
 * here comes only after the password, as the second factor on a new device.
 */
import type { MerchantAuth, Session } from "@damga/kit";
import { Offline } from "@damga/kit";
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";

export type MerchantSignInWords = {
  title: string;
  body: string;
  username: string;
  password: string;
  showPassword: string;
  hidePassword: string;
  submit: string;
  working: string;
  refused: string;
  codeTitle: string;
  codeBody: string;
  codeLabel: string;
  verify: string;
  codeRefused: string;
  codeIncomplete: string;
  startAgain: string;
  tooManyCodes: string;
  mailDown: string;
  unreachable: string;
};

export function MerchantSignIn({
  auth,
  words,
  brand,
  initialUsername = "",
  onSignedIn,
}: {
  auth: MerchantAuth;
  words: MerchantSignInWords;
  brand?: ReactNode;
  initialUsername?: string;
  onSignedIn: (session: Session) => void;
}) {
  const [step, setStep] = useState<"password" | "code">("password");
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState("");
  const [shown, setShown] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const passwordId = useId();

  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !username.trim() || !password) return;
    setBusy(true);
    setProblem(null);
    try {
      const out = await auth.signIn(username.trim(), password);
      if (out.kind === "signed_in") {
        setPassword("");
        onSignedIn(out.session);
      } else if (out.kind === "code_sent") {
        setPassword("");
        setChallenge(out.challenge);
        setStep("code");
      } else if (out.kind === "too_many_codes") {
        setProblem(words.tooManyCodes);
      } else if (out.kind === "mail_down") {
        setProblem(words.mailDown);
      } else {
        setProblem(words.refused);
      }
    } catch (err) {
      setProblem(err instanceof Offline ? words.unreachable : words.unreachable);
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    // SAID, NOT GREYED OUT. A disabled button fades below legibility and says
    // nothing about why; a sentence says what is missing.
    if (code.length !== 6) {
      setProblem(words.codeIncomplete);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const out = await auth.verifyCode(challenge, code);
      if (out.kind === "signed_in") onSignedIn(out.session);
      else setProblem(words.codeRefused);
    } catch {
      setProblem(words.unreachable);
    } finally {
      setBusy(false);
    }
  }

  function startAgain() {
    setStep("password");
    setCode("");
    setChallenge("");
    setProblem(null);
  }

  return (
    <div className="msi">
      <div className="msi-card">
        {brand}
        {step === "password" ? (
          <form className="msi-form" onSubmit={submit} noValidate>
            <h1 className="msi-title">{words.title}</h1>
            <p className="msi-body">{words.body}</p>
            <label className="msi-field">
              <span className="msi-label">{words.username}</span>
              <input
                className="msi-input"
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </label>
            {/* The reveal button is BESIDE the label, not in it: inside, its
                words became part of the field's name, and a screen reader said
                "Password Show". Found by the till's render proof. */}
            <div className="msi-field">
              <label className="msi-label" htmlFor={passwordId}>
                {words.password}
              </label>
              <span className="msi-password">
                <input
                  id={passwordId}
                  className="msi-input"
                  name="password"
                  type={shown ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="msi-reveal"
                  aria-pressed={shown}
                  aria-controls={passwordId}
                  onClick={() => setShown((v) => !v)}
                >
                  {shown ? words.hidePassword : words.showPassword}
                </button>
              </span>
            </div>
            {problem && (
              <p className="msi-problem" role="alert">
                {problem}
              </p>
            )}
            <button className="msi-submit" type="submit" aria-busy={busy}>
              {busy ? words.working : words.submit}
            </button>
          </form>
        ) : (
          <form className="msi-form" onSubmit={verify} noValidate>
            <h1 className="msi-title">{words.codeTitle}</h1>
            <p className="msi-body">{words.codeBody}</p>
            <label className="msi-field">
              <span className="msi-label">{words.codeLabel}</span>
              <input
                ref={codeRef}
                className="msi-input msi-code"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </label>
            {problem && (
              <p className="msi-problem" role="alert">
                {problem}
              </p>
            )}
            <button className="msi-submit" type="submit" aria-busy={busy}>
              {busy ? words.working : words.verify}
            </button>
            <button type="button" className="msi-link" onClick={startAgain}>
              {words.startAgain}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* Layout only, from tokens; each register colours it. Every target is 44px or
 * more, and nothing is wider than the screen at 320. */
export const merchantSignInCss = `
.msi { min-height: 100dvh; display: grid; place-items: center; padding: 24px 16px; box-sizing: border-box; }
.msi-card { width: 100%; max-width: 400px; display: grid; gap: 20px; }
.msi-form { display: grid; gap: 16px; }
.msi-title { margin: 0; font-size: 24px; line-height: 1.2; text-wrap: balance; }
.msi-body { margin: 0; line-height: 1.5; }
.msi-field { display: grid; gap: 6px; }
.msi-label { font-size: 13px; font-weight: 600; }
.msi-input { box-sizing: border-box; width: 100%; min-height: 48px; padding: 12px 14px; font: inherit; font-size: 16px; border-radius: 12px; border: 1px solid currentColor; background: transparent; color: inherit; }
.msi-password { position: relative; display: block; }
.msi-password .msi-input { padding-right: 88px; }
.msi-reveal { position: absolute; right: 4px; top: 4px; bottom: 4px; min-width: 76px; min-height: 40px; border: 0; background: transparent; color: inherit; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; border-radius: 10px; }
.msi-code { font-variant-numeric: tabular-nums; letter-spacing: .3em; font-size: 22px; text-align: center; }
.msi-problem { margin: 0; line-height: 1.45; font-weight: 600; }
/* The register's primary button, from the same tokens the ds Button reads, and
   never faded: a busy button still says "One moment" at full strength. */
.msi-submit { min-height: 48px; font: inherit; font-weight: 700; font-size: 15px; cursor: pointer; border: 0;
  background: var(--button-primary-bg, var(--ink)); color: var(--button-primary-fg, var(--paper));
  border-radius: var(--button-primary-radius, 14px); box-shadow: var(--button-primary-shadow, none); }
.msi-submit[aria-busy="true"] { cursor: progress; }
.msi-link { min-height: 44px; border: 0; background: transparent; color: inherit; font: inherit; font-size: 14px; text-decoration: underline; cursor: pointer; justify-self: center; }
`;
